import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { TrustBanner, HeroTrustStats, TrustBadges, LiveActivityFeed, StripeSecurityBadge } from '../components/TrustSignals';
import { FomoNotifications } from '../components/FomoNotifications';
import { JobGallery, CheckrTrustSection } from '../components/JobGallery';
import LeadCaptureForm from '../components/LeadCaptureForm';
import EditableText from '../components/EditableText';
import EditableImage from '../components/EditableImage';
import { Car, MapPin, Clock, Shield, Phone, Wrench, Battery, Key, Truck, ShoppingBag, Fuel, Zap, Settings, Droplets, Wind, CheckCircle, Star, Users, CreditCard, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';

const EMERGENCY_PHONE = '2602183536';

const roadsideServices = [
  { icon: Truck, title: 'Towing', desc: '24/7 towing to your preferred location', price: '$150', type: 'tow' },
  { icon: Battery, title: 'Jump Start', desc: 'Battery dead? We\'ll get you running', price: '$50', type: 'jump_start' },
  { icon: Car, title: 'Flat Tire Change', desc: 'Quick tire change on the spot', price: '$75', type: 'flat_tire' },
  { icon: Key, title: 'Lockout Service', desc: 'Locked out? We\'ll get you in', price: '$60', type: 'lockout' },
  { icon: Fuel, title: 'Fuel Delivery', desc: 'Emergency fuel delivery', price: '$45', type: 'fuel_delivery' },
  { icon: Zap, title: 'Winch Out', desc: 'Vehicle stuck? We\'ll pull you out', price: '$125', type: 'winch_out' },
];

const mechanicServices = [
  { icon: Shield, title: 'Diagnostic', desc: 'Complete vehicle diagnostic', price: '$100', type: 'diagnostic' },
  { icon: Droplets, title: 'Oil Change', desc: 'Oil and filter change', price: '$65', type: 'oil_change' },
  { icon: Settings, title: 'Brake Repair', desc: 'Brake pad/rotor replacement', price: '$250', type: 'brake_repair' },
  { icon: Wrench, title: 'General Repair', desc: 'On-site mechanical repairs', price: '$150+', type: 'general_repair' },
  { icon: Wind, title: 'AC Repair', desc: 'Air conditioning repair', price: '$200', type: 'ac_repair' },
  { icon: Battery, title: 'Battery Replace', desc: 'New battery installation', price: '$150', type: 'battery_replacement' },
];

const features = [
  { icon: Clock, title: '24/7 Availability', desc: 'Round-the-clock service whenever you need it' },
  { icon: MapPin, title: 'Real-Time Tracking', desc: 'Watch your technician arrive in real-time' },
  { icon: Shield, title: 'Secure Payments', desc: 'Stripe protected transactions' },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCallModal, setShowCallModal] = useState(false);

  useEffect(() => {
    if (user) {
      // Redirect to appropriate dashboard based on user type
      let path = '/customer';
      if (user.is_owner || user.user_type === 'owner') {
        path = '/owner';
      } else if (user.is_admin || user.user_type === 'admin') {
        path = '/admin';
      } else if (user.user_type === 'mechanic' || user.user_type === 'roadside_tech' || user.user_type === 'roadside_provider') {
        path = '/provider';
      }
      navigate(path);
    }
  }, [user, navigate]);

  const handleEmergencyCall = () => {
    window.location.href = `tel:${EMERGENCY_PHONE}`;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Emergency Call Banner */}
      <div className="bg-[#FF3008] text-white py-2 text-center">
        <a 
          href={`tel:${EMERGENCY_PHONE}`} 
          className="flex items-center justify-center gap-2 font-semibold hover:underline"
          data-testid="emergency-banner"
        >
          <Phone className="w-4 h-4 animate-pulse" />
          Emergency? Call Now: (260) 218-3536
        </a>
      </div>

      {/* Header - Clean White */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
              <div className="w-10 h-10 bg-[#FF3008] rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-white font-extrabold text-lg">J</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-extrabold text-gray-900 leading-none">Jiffys</span>
                <span className="text-[10px] font-medium text-gray-500 tracking-wide">ASSISTANCE</span>
              </div>
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/marketplace" className="text-gray-700 hover:text-gray-900 font-medium hidden sm:block" data-testid="landing-marketplace">
                Marketplace
              </Link>
              <Link to="/food" className="text-[#FF3008] hover:text-[#D42600] font-medium hidden sm:block" data-testid="landing-food">
                Get it Delivered
              </Link>
              <Link to="/apply" className="text-gray-700 hover:text-gray-900 font-medium hidden sm:block" data-testid="landing-apply">
                Join Our Team
              </Link>
              <Link to="/login" className="text-gray-700 hover:text-gray-900 font-medium" data-testid="landing-login">
                Login
              </Link>
              <Link to="/register" data-testid="landing-register">
                <Button className="bg-[#FF3008] hover:bg-[#D42600] text-white rounded-full px-6">
                  Get Started
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1686966933735-305bd8fe0a77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHx0b3clMjB0cnVja3xlbnwwfHx8fDE3NzQ1MDU4MTd8MA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3008] mb-4 animate-fade-in-up opacity-0 animate-stagger-1">
              <EditableText
                brandKey="footer_company_name"
                defaultValue="Jiffys Assistance"
                as="span"
              />
              {' - '}
              <EditableText
                brandKey="footer_location"
                defaultValue="Fort Wayne, IN"
                as="span"
              />
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white mb-6 animate-fade-in-up opacity-0 animate-stagger-2">
              Stranded?<br />Help is Minutes Away
            </h1>
            <p className="text-lg text-gray-200 mb-8 leading-relaxed animate-fade-in-up opacity-0 animate-stagger-3">
              Connect with certified mechanics instantly. Whether it's a flat tire, dead battery, 
              or engine trouble—we've got you covered anywhere, anytime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0 animate-stagger-4">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button size="lg" className="w-full sm:w-auto bg-[#FF3008] hover:bg-[#D42600] text-white text-lg px-8 py-6 rounded-full font-bold">
                  Request Assistance Now
                </Button>
              </Link>
              <a href={`tel:${EMERGENCY_PHONE}`} data-testid="hero-call-btn">
                <Button size="lg" className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-sm font-semibold">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
            
            {/* Hero Trust Stats */}
            <div className="mt-10 animate-fade-in-up opacity-0 animate-stagger-5">
              <HeroTrustStats />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <TrustBanner />

      {/* Roadside Assistance Services */}
      <section className="py-24 bg-gray-50" data-testid="roadside-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-red-600 mb-2">Emergency Roadside</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 font-['Chivo']">
              Roadside Assistance Services
            </h2>
            <p className="text-gray-600 mt-2">24/7 emergency help when you need it most</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roadsideServices.map((service, idx) => (
              <Link 
                key={service.title}
                to={`/register?service=${service.type}`}
                className="bg-white p-8 border border-gray-200 rounded-sm card-hover block"
                data-testid={`roadside-service-${idx}`}
              >
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 font-['Chivo']">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.desc}</p>
                <p className="text-red-600 font-bold text-lg">Starting at {service.price} <span className="text-gray-400 text-sm font-normal">+ 7% tax</span></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Mechanic Services */}
      <section className="py-24 bg-white" data-testid="mechanic-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3008] mb-2">Mobile Mechanic</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#191919]">
              On-Site Repair Services
            </h2>
            <p className="text-gray-600 mt-2">Professional repairs at your location</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mechanicServices.map((service, idx) => (
              <Link 
                key={service.title}
                to={`/register?service=${service.type}`}
                className="bg-gray-50 p-8 border border-gray-200 rounded-sm card-hover block"
                data-testid={`mechanic-service-${idx}`}
              >
                <div className="w-14 h-14 bg-[#FFF0ED] rounded-lg flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#FF3008]" />
                </div>
                <h3 className="text-xl font-semibold text-[#191919] mb-2">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.desc}</p>
                <p className="text-[#FF3008] font-bold text-lg">Starting at {service.price} <span className="text-gray-400 text-sm font-normal">+ 7% tax</span></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3008] mb-2">Why Choose Us</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#191919] mb-8">
                Fast, Reliable, Professional
              </h2>
              
              <div className="space-y-8">
                {features.map((feature) => (
                  <div key={feature.title} className="flex gap-4">
                    <div className="w-12 h-12 bg-[#FF3008] rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#191919] mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/33488/navigation-car-drive-road.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Real-time tracking"
                className="rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-sm shadow-xl border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Mechanic ETA</p>
                    <p className="text-2xl font-bold text-gray-900 font-['Chivo']">12 min</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkr Background Check Trust Section */}
      <CheckrTrustSection />

      {/* Job Gallery - Completed Work Photos */}
      <JobGallery />

      {/* Marketplace Section */}
      <section className="py-24 bg-white" data-testid="marketplace-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#1a56db] mb-2">Buy & Sell</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 font-['Chivo'] mb-4">
              Marketplace
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Buy and sell auto parts, tools, vehicles, and accessories. 
              Connect directly with sellers and get great deals.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Chivo']">Buy Anything</h3>
              <p className="text-gray-600">Find auto parts, tools, vehicles, and more from trusted sellers</p>
            </div>
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Chivo']">Sell & Earn</h3>
              <p className="text-gray-600">List your items and earn money. Only 1% platform fee on sales</p>
            </div>
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Chivo']">Message & Comment</h3>
              <p className="text-gray-600">Communicate with buyers and sellers directly on listings</p>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/marketplace" data-testid="explore-marketplace">
              <Button size="lg" className="bg-[#FF3008] hover:bg-[#D42600] text-white text-lg px-8 py-6 rounded-full font-bold">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Explore Marketplace
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Join Our Team Section */}
      <section className="py-24 bg-gray-900" data-testid="join-team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3008] mb-2">Join Our Team</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white mb-4 font-['Chivo']">
              Become a Service Provider
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Set your own schedule, accept jobs in your area, and earn money helping people in need.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mobile Mechanic */}
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
              <div className="w-16 h-16 bg-[#FF3008] rounded-xl flex items-center justify-center mb-6">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 font-['Chivo']">Mobile Mechanic</h3>
              <p className="text-gray-300 mb-6">
                Provide on-site repairs, diagnostics, oil changes, brake work, and more. 
                Bring your skills directly to customers.
              </p>
              <ul className="space-y-2 mb-6 text-gray-300">
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Flexible hours
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Competitive pay
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Use your own tools & vehicle
                </li>
              </ul>
              <Link to="/apply?type=mechanic" data-testid="apply-mechanic-btn">
                <Button className="w-full bg-[#FF3008] hover:bg-[#D42600] text-white py-6 rounded-full font-bold">
                  Apply as Mobile Mechanic
                </Button>
              </Link>
            </div>

            {/* Roadside Assistance Provider */}
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
              <div className="w-16 h-16 bg-red-600 rounded-lg flex items-center justify-center mb-6">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 font-['Chivo']">Roadside Assistance Provider</h3>
              <p className="text-gray-300 mb-6">
                Provide towing, jump starts, lockout services, tire changes, and emergency assistance 
                to drivers in need.
              </p>
              <ul className="space-y-2 mb-6 text-gray-300">
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  24/7 opportunities
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Instant payments
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Tow truck or service vehicle required
                </li>
              </ul>
              <Link to="/apply?type=roadside_provider" data-testid="apply-roadside-btn">
                <Button className="w-full bg-[#FF3008] hover:bg-[#D42600] text-white py-6 rounded-full font-bold">
                  Apply as Roadside Provider
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture Section */}
      <section className="py-20 bg-white border-t border-gray-100" data-testid="lead-capture-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-gray-900 mb-3">
              Get a Free Quote
            </h2>
            <p className="text-gray-600">
              Tell us what you need and we'll get back to you within minutes.
            </p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
            <LeadCaptureForm />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#FF3008]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white mb-4">
            Need Help Right Now?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Call our 24/7 hotline for immediate assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" data-testid="cta-get-started">
              <Button size="lg" className="w-full sm:w-auto bg-white text-[#FF3008] hover:bg-gray-100 text-lg px-8 py-6 rounded-full font-bold">
                Get Started Free
              </Button>
            </Link>
            <a href={`tel:${EMERGENCY_PHONE}`} data-testid="cta-call">
              <Button size="lg" className="w-full sm:w-auto bg-[#191919] hover:bg-black text-white text-lg px-8 py-6 rounded-full font-bold">
                <Phone className="w-5 h-5 mr-2" />
                (260) 218-3536
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <EditableImage
                  brandKey="logo_image_url"
                  imgClassName="w-8 h-8 rounded-lg object-contain"
                  fallback={
                    <div className="w-8 h-8 bg-[#FF3008] rounded-lg flex items-center justify-center">
                      <EditableText
                        brandKey="logo_letter"
                        defaultValue="J"
                        className="text-white font-extrabold text-sm"
                      />
                    </div>
                  }
                  testId="footer-logo-image"
                />
                <EditableText
                  brandKey="footer_company_name"
                  defaultValue="Jiffys Assistance"
                  className="text-lg font-bold text-white"
                  testId="footer-app-name"
                />
              </div>
              <p className="text-gray-400 text-sm">
                <EditableText
                  brandKey="marketing_blurb"
                  defaultValue="24/7 roadside assistance and mobile mechanic services in Fort Wayne, IN and surrounding areas."
                  multiline
                  testId="footer-marketing-blurb"
                />
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Towing</li>
                <li>Jump Start</li>
                <li>Flat Tire</li>
                <li>Lockout</li>
                <li>Mobile Repair</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/apply" className="hover:text-white">Join Our Team</Link></li>
                <li><Link to="/marketplace" className="hover:text-white">Marketplace</Link></li>
                <li><Link to="/login" className="hover:text-white">Login</Link></li>
                <li><Link to="/download" className="hover:text-white text-green-400">Download App</Link></li>
                <li><Link to="/advertiser/login" className="hover:text-white text-yellow-400/80">Advertise With Us</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <a href={`tel:${EMERGENCY_PHONE}`} className="hover:text-white flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    (260) 218-3536
                  </a>
                </li>
                <li>Fort Wayne, IN</li>
                <li>Available 24/7</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              &copy; 2026 Jiffys Assistance. All rights reserved.
            </p>
            <div className="flex gap-4 mt-2 md:mt-0">
              <Link to="/privacy" className="text-gray-500 text-xs hover:text-white">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-500 text-xs hover:text-white">Terms of Service</Link>
              <span className="text-gray-500 text-xs">Fort Wayne, IN &amp; 50-mile radius</span>
            </div>
          </div>
        </div>
      </footer>

      {/* FOMO Notifications - Social Proof Popups */}
      <FomoNotifications 
        enabled={true}
        interval={45000}
        initialDelay={8000}
      />
    </div>
  );
}
